import type { Category } from '../types/Product';
import type { ServiceResponse } from './types';
import { apiRequest } from '../utils/api';
import { API_ENDPOINTS } from '../config/api.config';
import { createCachedRequest } from '../utils/requestDeduplication';

// Backend category response type
interface CategoryResponse {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  parentCategory?: string;
  imageUrl?: string;
  isActive: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
  productsCount?: number;
  children?: CategoryResponse[];
}

class CategoryService {
  // Transform backend category to frontend format
  private transformCategory(category: CategoryResponse): Category {
    return {
      id: category._id,
      name: category.name,
      slug: category.slug,
      image: category.imageUrl
    };
  }

  // Internal method to fetch categories
  private async _getCategories(): Promise<ServiceResponse<Category[]>> {
    try {
      const response = await apiRequest<any>(API_ENDPOINTS.CATEGORIES.BASE);
      
      // Handle nested data structure from API
      const categories = response.data || response;
      
      if (!Array.isArray(categories)) {
        console.error('Unexpected categories response format:', response);
        return {
          success: false,
          data: [],
          error: 'Invalid response format'
        };
      }
      
      // Filter active categories and transform
      const transformedCategories = categories
        .filter((cat: CategoryResponse) => cat.isActive)
        .map((cat: CategoryResponse) => this.transformCategory(cat))
        .sort((a, b) => a.name.localeCompare(b.name));

      return {
        success: true,
        data: transformedCategories
      };
    } catch (error) {
      console.error('Error fetching categories:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Failed to fetch categories'
      };
    }
  }

  // Get category tree (hierarchical)
  async getCategoryTree(): Promise<ServiceResponse<CategoryResponse[]>> {
    try {
      const tree = await apiRequest<CategoryResponse[]>(API_ENDPOINTS.CATEGORIES.TREE);
      
      return {
        success: true,
        data: tree
      };
    } catch (error) {
      console.error('Error fetching category tree:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Failed to fetch category tree'
      };
    }
  }

  // Get category by slug
  async getCategoryBySlug(slug: string): Promise<ServiceResponse<Category | null>> {
    try {
      const category = await apiRequest<CategoryResponse>(API_ENDPOINTS.CATEGORIES.BY_SLUG(slug));
      
      if (!category || !category.isActive) {
        return {
          success: false,
          data: null,
          error: 'Category not found'
        };
      }

      return {
        success: true,
        data: this.transformCategory(category)
      };
    } catch (error) {
      console.error('Error fetching category by slug:', error);
      return {
        success: false,
        data: null,
        error: error instanceof Error ? error.message : 'Failed to fetch category'
      };
    }
  }

  // Get category by ID
  async getCategoryById(id: string): Promise<ServiceResponse<Category | null>> {
    try {
      const category = await apiRequest<CategoryResponse>(API_ENDPOINTS.CATEGORIES.BY_ID(id));
      
      if (!category || !category.isActive) {
        return {
          success: false,
          data: null,
          error: 'Category not found'
        };
      }

      return {
        success: true,
        data: this.transformCategory(category)
      };
    } catch (error) {
      console.error('Error fetching category by ID:', error);
      return {
        success: false,
        data: null,
        error: error instanceof Error ? error.message : 'Failed to fetch category'
      };
    }
  }

  // Get child categories
  async getChildCategories(parentId: string): Promise<ServiceResponse<Category[]>> {
    try {
      const children = await apiRequest<CategoryResponse[]>(API_ENDPOINTS.CATEGORIES.CHILDREN(parentId));
      
      const transformedCategories = children
        .filter(cat => cat.isActive)
        .map(cat => this.transformCategory(cat))
        .sort((a, b) => a.name.localeCompare(b.name));

      return {
        success: true,
        data: transformedCategories
      };
    } catch (error) {
      console.error('Error fetching child categories:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Failed to fetch child categories'
      };
    }
  }
  
  // Create deduped version of getCategories
  getCategories = createCachedRequest(
    this._getCategories.bind(this),
    () => 'categories-all',
    60000 // Cache for 1 minute
  );
}

export const categoryService = new CategoryService();